/**
 * Panel geometry: box with 0.6 mm chamfered arrises, rewritten in place.
 * Local axes: x = length (grain), y = width, z = thickness.
 * Group 0 = face material (faces + face-side arrises), group 1 = ply edge.
 * Port of design/shared/viewer.js.
 */
import * as THREE from 'three';
import type { Part } from '@/domain';

export const FACE_TILE_M = 1.0; // physical size of the face texture (provenance.json)
export const EDGE_TILE_M = 0.6; // along-edge repeat of the ply section texture
export const ARRIS_M = 0.0006;

/** Placement in metres: minimum corner and size. */
export interface Box6 {
  x: number;
  y: number;
  z: number;
  sx: number;
  sy: number;
  sz: number;
}

type Vec3 = [number, number, number];
type Vec2 = [number, number];
interface Prim {
  g: 0 | 1;
  p: Vec3[];
  n: Vec3[];
  uv: Vec2[];
}
interface PanelOut {
  pos: Float32Array;
  uv: Float32Array;
  nor?: Float32Array;
}

const PANEL_VERTS = 96;

function emitPanel(L: number, W: number, T: number, ou: number, ov: number, out: PanelOut): Prim[] {
  const A = L / 2,
    B = W / 2,
    C = T / 2,
    c = Math.min(ARRIS_M, C * 0.4, A * 0.4, B * 0.4);
  const prims: Prim[] = [];
  const faceUV = (p: Vec3): Vec2 => [p[0] / FACE_TILE_M + ou, p[1] / FACE_TILE_M + ov];
  const edgeUVx = (p: Vec3): Vec2 => [p[0] / EDGE_TILE_M + ou * 3, (p[2] + C) / (2 * C)];
  const edgeUVy = (p: Vec3): Vec2 => [p[1] / EDGE_TILE_M + ov * 3, (p[2] + C) / (2 * C)];
  const quad = (grp: 0 | 1, p: Vec3[], n: Vec3[], uvf: (p: Vec3) => Vec2) => prims.push({ g: grp, p, n, uv: p.map(uvf) });
  const four = (v: Vec3): Vec3[] => [v, v, v, v];
  for (const sz of [1, -1]) {
    const z = sz * C;
    quad(0, [[-A + c, -B + c, z], [A - c, -B + c, z], [A - c, B - c, z], [-A + c, B - c, z]], four([0, 0, sz]), faceUV);
  }
  for (const sy of [1, -1])
    for (const sz of [1, -1]) {
      quad(
        0,
        [[-A + c, sy * (B - c), sz * C], [A - c, sy * (B - c), sz * C], [A - c, sy * B, sz * (C - c)], [-A + c, sy * B, sz * (C - c)]],
        [[0, 0, sz], [0, 0, sz], [0, sy, 0], [0, sy, 0]],
        faceUV,
      );
    }
  for (const sx of [1, -1])
    for (const sz of [1, -1]) {
      quad(
        0,
        [[sx * (A - c), -B + c, sz * C], [sx * (A - c), B - c, sz * C], [sx * A, B - c, sz * (C - c)], [sx * A, -B + c, sz * (C - c)]],
        [[0, 0, sz], [0, 0, sz], [sx, 0, 0], [sx, 0, 0]],
        faceUV,
      );
    }
  for (const sy of [1, -1]) {
    quad(1, [[-A + c, sy * B, -C + c], [A - c, sy * B, -C + c], [A - c, sy * B, C - c], [-A + c, sy * B, C - c]], four([0, sy, 0]), edgeUVx);
  }
  for (const sx of [1, -1]) {
    quad(1, [[sx * A, -B + c, -C + c], [sx * A, B - c, -C + c], [sx * A, B - c, C - c], [sx * A, -B + c, C - c]], four([sx, 0, 0]), edgeUVy);
  }
  for (const sx of [1, -1])
    for (const sy of [1, -1]) {
      quad(
        1,
        [[sx * (A - c), sy * B, -C + c], [sx * A, sy * (B - c), -C + c], [sx * A, sy * (B - c), C - c], [sx * (A - c), sy * B, C - c]],
        [[0, sy, 0], [sx, 0, 0], [sx, 0, 0], [0, sy, 0]],
        edgeUVx,
      );
    }
  for (const sx of [1, -1])
    for (const sy of [1, -1])
      for (const sz of [1, -1]) {
        const p: Vec3[] = [[sx * (A - c), sy * (B - c), sz * C], [sx * A, sy * (B - c), sz * (C - c)], [sx * (A - c), sy * B, sz * (C - c)]];
        prims.push({ g: 1, p, n: [[0, 0, sz], [sx, 0, 0], [0, sy, 0]], uv: p.map(edgeUVx) });
      }
  let v = 0;
  for (const pr of prims)
    for (let k = 0; k < pr.p.length; k++, v++) {
      out.pos[v * 3] = pr.p[k][0];
      out.pos[v * 3 + 1] = pr.p[k][1];
      out.pos[v * 3 + 2] = pr.p[k][2];
      out.uv[v * 2] = pr.uv[k][0];
      out.uv[v * 2 + 1] = pr.uv[k][1];
      if (out.nor) {
        out.nor[v * 3] = pr.n[k][0];
        out.nor[v * 3 + 1] = pr.n[k][1];
        out.nor[v * 3 + 2] = pr.n[k][2];
      }
    }
  return prims;
}

interface Topology {
  index: number[];
  groups: Array<[number, number, number]>;
  normals: Float32Array;
}
let PANEL_TOPOLOGY: Topology | null = null;

function panelTopology(): Topology {
  if (PANEL_TOPOLOGY) return PANEL_TOPOLOGY;
  const pos = new Float32Array(PANEL_VERTS * 3),
    uv = new Float32Array(PANEL_VERTS * 2),
    nor = new Float32Array(PANEL_VERTS * 3);
  const prims = emitPanel(0.5, 0.3, 0.018, 0, 0, { pos, uv, nor });
  const idx: Record<0 | 1, number[]> = { 0: [], 1: [] };
  let v = 0;
  const a = new THREE.Vector3(),
    b = new THREE.Vector3(),
    cc = new THREE.Vector3(),
    n = new THREE.Vector3();
  for (const pr of prims) {
    const base = v;
    a.fromArray(pr.p[0]);
    b.fromArray(pr.p[1]);
    cc.fromArray(pr.p[2]);
    const face = b.clone().sub(a).cross(cc.clone().sub(a));
    n.set(0, 0, 0);
    for (const nn of pr.n) n.add(new THREE.Vector3().fromArray(nn));
    const flip = face.dot(n) < 0;
    if (pr.p.length === 4) {
      idx[pr.g].push(...(flip ? [base, base + 2, base + 1, base, base + 3, base + 2] : [base, base + 1, base + 2, base, base + 2, base + 3]));
    } else {
      idx[pr.g].push(...(flip ? [base, base + 2, base + 1] : [base, base + 1, base + 2]));
    }
    v += pr.p.length;
  }
  PANEL_TOPOLOGY = {
    index: [...idx[0], ...idx[1]],
    groups: [
      [0, idx[0].length, 0],
      [idx[0].length, idx[1].length, 1],
    ],
    normals: nor,
  };
  return PANEL_TOPOLOGY;
}

export function createPanelGeometry(): THREE.BufferGeometry {
  const topo = panelTopology();
  const g = new THREE.BufferGeometry();
  g.setIndex(topo.index);
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(PANEL_VERTS * 3), 3).setUsage(THREE.DynamicDrawUsage));
  g.setAttribute('normal', new THREE.BufferAttribute(topo.normals.slice(), 3));
  g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(PANEL_VERTS * 2), 2).setUsage(THREE.DynamicDrawUsage));
  for (const [s, c, m] of topo.groups) g.addGroup(s, c, m);
  return g;
}

export function writePanelGeometry(g: THREE.BufferGeometry, L: number, W: number, T: number, ou: number, ov: number): void {
  const pos = g.getAttribute('position') as THREE.BufferAttribute;
  const uv = g.getAttribute('uv') as THREE.BufferAttribute;
  emitPanel(Math.max(L, 1e-4), Math.max(W, 1e-4), Math.max(T, 1e-4), ou, ov, { pos: pos.array as Float32Array, uv: uv.array as Float32Array });
  pos.needsUpdate = true;
  uv.needsUpdate = true;
  g.computeBoundingSphere();
  g.computeBoundingBox();
}

/** Local → world basis per role (proper rotations; columns are the world images of local x, y, z). */
export const BASIS = {
  side: [[0, 1, 0], [0, 0, 1], [1, 0, 0]],
  horizontal: [[1, 0, 0], [0, 0, -1], [0, 1, 0]],
  backV: [[0, 1, 0], [-1, 0, 0], [0, 0, 1]],
  backH: [[1, 0, 0], [0, 1, 0], [0, 0, 1]],
} as const satisfies Record<string, readonly (readonly [number, number, number])[]>;
export type BasisKey = keyof typeof BASIS;

/**
 * Panel basis from the part's role (BoardAssembly roles are open-ended): boards standing with
 * their thickness across x (sides, dividers, end panels, posts) use 'side'; boards facing the
 * front (backs, plinths, back blocks) use 'backV'/'backH' by grain; everything else lies flat.
 */
export function basisFor(part: Pick<Part, 'role' | 'grainWorld'>): BasisKey {
  if (part.role === 'side' || part.role === 'divider' || part.role === 'end' || part.role === 'post' || part.role === 'drawer-side') return 'side';
  // doors, drawer fronts and their frames face the front like a back (src/families/fronts.ts)
  if (['back', 'plinth', 'block', 'door', 'door-stile', 'door-rail', 'glass', 'slider', 'drawer-front', 'drawer-rear'].includes(part.role)) return part.grainWorld === 'y' ? 'backV' : 'backH';
  return 'horizontal';
}

/** p = {sx, sy, sz} metres → [L, W, T] in the panel's local frame. */
export function localDims(key: BasisKey, p: Box6): [number, number, number] {
  switch (key) {
    case 'side':
      return [p.sy, p.sz, p.sx];
    case 'horizontal':
      return [p.sx, p.sz, p.sy];
    case 'backV':
      return [p.sy, p.sx, p.sz];
    default:
      return [p.sx, p.sy, p.sz];
  }
}
